<script setup lang="ts">
	import { Float } from '@headlessui-float/vue'
	import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
	import {
		CheckIcon,
		DevicePhoneMobileIcon,
		EnvelopeIcon,
		PlusIcon,
	} from '@heroicons/vue/24/outline'
	import {
		EllipsisVerticalIcon,
		PencilSquareIcon,
	} from '@heroicons/vue/24/solid'
	import { useQueryClient } from '@tanstack/vue-query'
	import { ref } from 'vue'

	import HasPermission from '@/components/HasPermission.vue'
	import ModalComponent from '@/components/ModalComponent.vue'

	import useEmail from '@/composables/useEmail'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'
	import usePhoneRequest from '@/composables/usePhoneRequest'

	import { CountryCode } from '@/interfaces/countryCode'
	import { Settings } from '@/interfaces/notificationSettings'

	import useUser from '../composables/useUser'
	import EditUserEmails from './EditUserEmails.vue'
	import { ToggleValue } from './EditUserNotifications.vue'
	import EditUserPhones from './EditUserPhones.vue'

	export type PhoneConverted = {
		numberWithCode: string
		number: string
		id: number
		countryId: number
		countryCode: CountryCode
	}

	export type EmailConverted = {
		email: string
		id: number
		isMain: boolean
		canDelete?: boolean
	}

	interface Props {
		userId: number
		settings: Settings
		title: string
		phones: PhoneConverted[]
		emails: EmailConverted[]
		type: 'services' | 'incentives'
	}

	interface Emits {
		(event: 'toggleValue', ...args: Parameters<ToggleValue>): void
		(event: 'handleSave'): Promise<void>
	}

	const queryClient = useQueryClient()

	const { hasPermissionUser } = useHasPermissionUser()

	const { deletePhoneAsync } = usePhoneRequest({
		immediate: false,
		params: {},
	})

	const { deleteEmailAsync } = useEmail({
		immediate: false,
		params: {},
	})

	const props = defineProps<Props>()
	const emit = defineEmits<Emits>()

	const checkInclusion = (value: string, type: keyof Settings) => {
		return props?.settings[type]?.includes(value)
	}

	const modalNumber = ref(false)

	const actionModalNumber = () => {
		modalNumber.value = !modalNumber.value
	}

	const modalEmail = ref(false)

	const actionModalEmail = () => {
		modalEmail.value = !modalEmail.value
	}

	const optionSelectedNumber = ref<PhoneConverted | undefined>(undefined)
	const optionSelectedEmail = ref<EmailConverted | undefined>(undefined)

	const selectedOptionNumber = (option: PhoneConverted) => {
		optionSelectedNumber.value = option

		actionModalNumber()
	}

	const selectedOptionEmail = (option: EmailConverted) => {
		optionSelectedEmail.value = option

		actionModalEmail()
	}

	const handleDeletePhone = async (id: number) => {
		await deletePhoneAsync({
			phoneId: id,
		})

		await queryClient.invalidateQueries(['userPhones', props.userId])
	}

	const handleDeleteEmail = async (id: string) => {
		await deleteEmailAsync({
			emailId: id,
		})

		await queryClient.invalidateQueries(['userEmails', props.userId])
	}
</script>

<template>
	<div class="space-y-4">
		<!-- Sección de WhatsApp -->
		<HasPermission
			:name-permission="'view-phones'"
			:entity-type="'App\\Models\\User'"
		>
			<div class="border border-gray-200 rounded-lg p-4">
				<div class="flex items-center justify-between mb-4">
					<div class="flex items-center space-x-2">
						<DevicePhoneMobileIcon class="w-5 h-5 text-gray-600" />
						<h5 class="text-sm font-medium text-gray-900">WhatsApp</h5>
					</div>

					<HasPermission
						:name-permission="'create-phones'"
						:entity-type="'App\\Models\\User'"
					>
						<button
							@click="actionModalNumber"
							class="flex items-center space-x-1 bg-gray-900 hover:bg-gray-800 text-white px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
						>
							<PlusIcon class="w-4 h-4" />
							<span>Agregar</span>
						</button>
					</HasPermission>
				</div>

				<!-- Lista de teléfonos -->
				<div
					v-if="phones.length > 0"
					class="space-y-2"
				>
					<div
						v-for="phone in phones"
						:key="phone.id"
						class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-green-300 transition-all duration-200"
					>
						<div class="flex items-center space-x-3">
							<button
								@click="
									() => {
										emit('toggleValue', phone.numberWithCode, type, 'whatsapp')
										emit('handleSave')
									}
								"
								class="w-5 h-5 rounded border-2 flex items-center justify-center transition-all"
								:class="
									checkInclusion(phone.numberWithCode, 'whatsapp') ?
										'bg-green-600 border-green-600 text-white'
									:	'border-gray-300 hover:border-green-400'
								"
								:disabled="
									!hasPermissionUser('update-phones', 'App\\Models\\User')
								"
							>
								<CheckIcon
									v-if="checkInclusion(phone.numberWithCode, 'whatsapp')"
									class="w-3 h-3"
								/>
							</button>

							<div class="flex items-center space-x-2">
								<div
									class="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center"
								>
									<span class="material-symbols-outlined text-green-600 text-xs"
										>phone</span
									>
								</div>
								<span class="text-sm text-gray-900"
									>+ {{ phone.numberWithCode }}</span
								>
							</div>
						</div>

						<!-- Menú de opciones para teléfonos -->
						<Menu
							as="div"
							class="relative"
							v-if="
								hasPermissionUser('update-phones', 'App\\Models\\User') &&
								hasPermissionUser('delete-phones', 'App\\Models\\User')
							"
						>
							<Float
								strategy="fixed"
								placement="bottom-end"
								:offset="4"
								enter="transition ease-out duration-100"
								enter-from="transform opacity-0 scale-95"
								enter-to="transform opacity-100 scale-100"
								leave="transition ease-in duration-75"
								leave-from="transform opacity-100 scale-100"
								leave-to="transform opacity-0 scale-95"
							>
								<MenuButton
									class="flex h-6 w-6 items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
								>
									<EllipsisVerticalIcon class="h-4 w-4" />
								</MenuButton>
								<MenuItems
									as="div"
									class="w-32 divide-y divide-gray-100 rounded-md shadow-lg ring-1 bg-white ring-black ring-opacity-5 focus:outline-none"
								>
									<div class="flex flex-col p-1">
										<MenuItem
											as="button"
											class="hover:bg-gray-100 rounded"
											v-if="
												hasPermissionUser('update-phones', 'App\\Models\\User')
											"
										>
											<button
												class="flex items-center p-2 text-sm w-full text-left"
												@click="selectedOptionNumber(phone)"
											>
												<PencilSquareIcon class="mr-2 h-4 w-4 text-gray-400" />
												<span>Editar</span>
											</button>
										</MenuItem>

										<MenuItem
											as="button"
											class="hover:bg-gray-100 rounded"
											v-if="
												hasPermissionUser('delete-phones', 'App\\Models\\User')
											"
										>
											<button
												class="flex items-center p-2 text-sm text-red-600 w-full text-left"
												@click="handleDeletePhone(phone.id)"
											>
												<span
													class="mr-2 h-4 w-4 material-symbols-outlined text-sm"
													>delete</span
												>
												<span>Eliminar</span>
											</button>
										</MenuItem>
									</div>
								</MenuItems>
							</Float>
						</Menu>
					</div>
				</div>

				<!-- Estado vacío para teléfonos -->
				<div
					v-else
					class="text-center py-6"
				>
					<div
						class="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-3"
					>
						<span class="material-symbols-outlined text-green-600 text-xl"
							>phone</span
						>
					</div>
					<p class="text-sm text-gray-500">No hay teléfonos registrados</p>
				</div>

				<!-- Modal para agregar/editar teléfono -->
				<ModalComponent
					:model-value="modalNumber"
					@update:model-value="actionModalNumber"
				>
					<template #modalBody>
						<div class="p-6">
							<EditUserPhones
								:user-id="userId.toString()"
								:is-notifications="true"
								@handle-action-modal="actionModalNumber"
								@handle-save="emit('handleSave')"
								:phone-selected="optionSelectedNumber"
							/>
						</div>
					</template>
				</ModalComponent>
			</div>
		</HasPermission>

		<!-- Sección de Correo Electrónico -->
		<HasPermission
			:name-permission="'view-emails'"
			:entity-type="'App\\Models\\User'"
		>
			<div class="border border-gray-200 rounded-lg p-4">
				<div class="flex items-center justify-between mb-4">
					<div class="flex items-center space-x-2">
						<EnvelopeIcon class="w-5 h-5 text-gray-600" />
						<h5 class="text-sm font-medium text-gray-900">
							Correo Electrónico
						</h5>
					</div>

					<HasPermission
						:name-permission="'create-emails'"
						:entity-type="'App\\Models\\User'"
					>
						<button
							@click="actionModalEmail"
							class="flex items-center space-x-1 bg-gray-900 hover:bg-gray-800 text-white px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
						>
							<PlusIcon class="w-4 h-4" />
							<span>Agregar</span>
						</button>
					</HasPermission>
				</div>

				<!-- Lista de emails -->
				<div
					v-if="emails.length > 0"
					class="space-y-2"
				>
					<template
						v-for="email in emails"
						:key="email.id"
					>
						<div
							v-if="email.email"
							class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-all duration-200"
						>
							<div
								class="flex items-center space-x-3"
								v-if="email.email"
							>
								<button
									@click="
										() => {
											emit('toggleValue', email.email, type, 'email')
											emit('handleSave')
										}
									"
									class="w-5 h-5 rounded border-2 flex items-center justify-center transition-all"
									:class="
										checkInclusion(email.email, 'email') ?
											'bg-blue-600 border-blue-600 text-white'
										:	'border-gray-300 hover:border-blue-400'
									"
									:disabled="
										!hasPermissionUser('update-phones', 'App\\Models\\User')
									"
								>
									<CheckIcon
										v-if="checkInclusion(email.email, 'email')"
										class="w-3 h-3"
									/>
								</button>

								<div class="flex items-center space-x-2">
									<div
										class="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center"
									>
										<span
											class="material-symbols-outlined text-blue-600 text-xs"
											>mail</span
										>
									</div>
									<div class="flex items-center space-x-2">
										<span class="text-sm text-gray-900">{{ email.email }}</span>
										<span
											v-if="email.isMain"
											class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
										>
											Principal
										</span>
									</div>
								</div>
							</div>

							<!-- Menú de opciones (solo para emails que se pueden eliminar) -->
							<Menu
								as="div"
								class="relative"
								v-if="
									email.canDelete !== false &&
									hasPermissionUser('update-emails', 'App\\Models\\User') &&
									hasPermissionUser('delete-emails', 'App\\Models\\User')
								"
							>
								<Float
									strategy="fixed"
									placement="bottom-end"
									:offset="4"
									enter="transition ease-out duration-100"
									enter-from="transform opacity-0 scale-95"
									enter-to="transform opacity-100 scale-100"
									leave="transition ease-in duration-75"
									leave-from="transform opacity-100 scale-100"
									leave-to="transform opacity-0 scale-95"
								>
									<MenuButton
										class="flex h-6 w-6 items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
									>
										<EllipsisVerticalIcon class="h-4 w-4" />
									</MenuButton>
									<MenuItems
										as="div"
										class="w-32 divide-y divide-gray-100 rounded-md shadow-lg ring-1 bg-white ring-black ring-opacity-5 focus:outline-none"
									>
										<div class="flex flex-col p-1">
											<MenuItem
												as="button"
												class="hover:bg-gray-100 rounded"
												v-if="
													hasPermissionUser(
														'update-emails',
														'App\\Models\\User'
													)
												"
											>
												<button
													class="flex items-center p-2 text-sm w-full text-left"
													@click="selectedOptionEmail(email)"
												>
													<PencilSquareIcon
														class="mr-2 h-4 w-4 text-gray-400"
													/>
													<span>Editar</span>
												</button>
											</MenuItem>

											<MenuItem
												as="button"
												class="hover:bg-gray-100 rounded"
												v-if="
													hasPermissionUser(
														'delete-emails',
														'App\\Models\\User'
													)
												"
											>
												<button
													class="flex items-center p-2 text-sm text-red-600 w-full text-left"
													@click="handleDeleteEmail(email.id.toString())"
												>
													<span
														class="mr-2 h-4 w-4 material-symbols-outlined text-sm"
														>delete</span
													>
													<span>Eliminar</span>
												</button>
											</MenuItem>
										</div>
									</MenuItems>
								</Float>
							</Menu>

							<!-- Indicador de email protegido -->
							<div
								v-else-if="email.isMain"
								class="flex items-center text-xs text-gray-500"
							>
								<span class="material-symbols-outlined text-sm mr-1">lock</span>
								<span>Protegido</span>
							</div>
						</div>
					</template>
				</div>

				<!-- Estado vacío para emails -->
				<div
					v-else
					class="text-center py-6"
				>
					<div
						class="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-3"
					>
						<span class="material-symbols-outlined text-blue-600 text-xl"
							>mail</span
						>
					</div>
					<p class="text-sm text-gray-500">
						No hay correos electrónicos registrados
					</p>
				</div>

				<!-- Modal para agregar/editar email -->
				<ModalComponent
					:model-value="modalEmail"
					@update:model-value="actionModalEmail"
				>
					<template #modalBody>
						<div class="p-6">
							<EditUserEmails
								:user-id="userId"
								@handle-action-modal="actionModalEmail"
								:email-selected="optionSelectedEmail"
							/>
						</div>
					</template>
				</ModalComponent>
			</div>
		</HasPermission>
	</div>
</template>

<style scoped>
	/* Estilos personalizados si son necesarios */
</style>
